// src/components/settings/claude-ai-tab.jsx
// One Claude (Anthropic) key for the whole CRM. Today it powers texting
// reply drafts; email drafting rides the same key next. Admin-only tab.
// Never log the raw key.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, CheckCircle2, KeyRound, Sparkles } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import {
  getAnthropicIntegration,
  saveAnthropicKey,
  saveVoiceProfile,
} from "../../utils/texting";

// Mirrors the backend cap in functions-api/index.js (buildAiReplyDraft slices
// the voice profile before it reaches Claude). Keep these two numbers in sync.
const VOICE_LIMIT = 12000;

export default function ClaudeAiTab() {
  const [{ user }] = useStateValue();
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [aiKey, setAiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState("");
  const [savingVoice, setSavingVoice] = useState(false);

  useEffect(() => {
    getAnthropicIntegration().then((a) => {
      setSaved(!!a?.apiKey);
      setVoice(a?.voiceProfile || "");
      setLoading(false);
    });
  }, []);

  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      await saveVoiceProfile(voice, user?.id);
      toast.success("Voice guide saved. Every AI draft now follows it.");
    } catch (e) {
      console.error("saveVoiceProfile failed:", e);
      toast.error("Could not save the voice guide. Try again.");
    } finally {
      setSavingVoice(false);
    }
  };

  const save = async () => {
    if (!aiKey.trim()) return;
    setBusy(true);
    try {
      await saveAnthropicKey(aiKey, user?.id);
      setAiKey("");
      setSaved(true);
      toast.success("Claude key saved. The ✨ buttons are live.");
    } catch (e) {
      console.error("saveAnthropicKey failed:", e);
      toast.error("Could not save the key. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6">
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Sparkles size={18} /> Claude AI
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          One key from console.anthropic.com powers every AI feature in the
          CRM. Drafts cost pennies and a human always approves before anything
          reaches a client.
        </p>
      </div>

      {saved && (
        <div className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={16} /> Connected
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {saved ? "Replace API Key" : "API Key"}
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <KeyRound
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="password"
              value={aiKey}
              onChange={(e) => setAiKey(e.target.value)}
              placeholder={
                saved
                  ? "Key saved ✓ (paste to replace)"
                  : "Paste your Anthropic API key…"
              }
              className="w-full text-sm rounded border border-slate-300 pl-8 pr-2 py-2"
            />
          </div>
          <button
            onClick={save}
            disabled={busy || !aiKey.trim()}
            className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
          >
            {busy ? "Saving…" : saved ? "Replace" : "Save Key"}
          </button>
        </div>
        <p className="text-[11px] text-slate-400">
          Get one at console.anthropic.com: sign in, add a small credit block
          under Billing, then API Keys → Create Key.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Henry's Voice
        </label>
        <p className="text-xs text-slate-500">
          Paste your voice and style guide here. Every AI draft follows it, so
          replies sound like you, not like a robot. Include phrases you use,
          phrases you'd never use, how you sign off, how casual to be.
        </p>
        <textarea
          rows={8}
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          placeholder={"Example:\nAlways warm and upbeat. Short sentences. Say \"Happy to help!\" a lot. Never pushy, never salesy. Sign off with just \"Henry\" when it fits."}
          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:border-themeColor outline-none"
        />
        {/* The backend only sends the first VOICE_LIMIT characters to Claude,
            so say it out loud rather than silently chopping the guide. */}
        <p
          className={`text-[11px] ${
            voice.length > VOICE_LIMIT
              ? "font-semibold text-red-600"
              : "text-slate-400"
          }`}
        >
          {voice.length.toLocaleString()} of {VOICE_LIMIT.toLocaleString()}{" "}
          characters
          {voice.length > VOICE_LIMIT
            ? `. Only the first ${VOICE_LIMIT.toLocaleString()} reach the AI, so trim it down or the rest gets cut off.`
            : ""}
        </p>
        <button
          onClick={saveVoice}
          disabled={savingVoice}
          className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
        >
          {savingVoice ? "Saving…" : "Save Voice Guide"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          What it powers
        </label>
        <ul className="text-sm text-slate-700 space-y-1.5">
          <li>
            ✨ <b>Texting replies</b>: the Suggest-reply button on every thread,
            plus drafted replies waiting in the Texting Center when clients
            text in (switch lives in the RingCentral tab).
          </li>
          <li className="text-slate-400">
            ✉️ <b>Email drafting</b> in the composer: coming next, same key.
          </li>
        </ul>
      </div>
    </div>
  );
}
